import React, { useState } from 'react';
import dayjs from 'dayjs';
import { useMutation } from '@apollo/client';
import RateAdjustmentSection, { RateRow } from './RateAdjustmentSection';
import MeterDetailsBlade from '../../MeterDetailsBlade';
import BladeSection from '../../BladeSection';
import styles from './AdjustmentsBlade.module.css';
import type { MeterMonthDetails, RateContractInfo } from '@pages/meter-read-details/useMeterReadDetailsPage';
import { SAVE_RATE_ADJUSTMENT, CLEAR_RATE_ADJUSTMENT } from '@graphql/meterReadDetails.gql';

interface AdjustmentsBladeProps {
  open: boolean;
  onClose: () => void;
  meterId: string;
  meterName?: string | null;
  meterNumber?: string | null;
  monthYear?: string;
  monthData: MeterMonthDetails | null;
  rateContracts?: RateContractInfo[];
  activeRateType?: string | null;
  isApproved?: boolean;
  energyBid?: number | null;
  runningContractTotal?: number | null;
  overageEmailSent?: boolean;
  onSaveMonth: (monthYear: string, input: Record<string, any>, reason?: string) => Promise<boolean>;
  onRateSaved?: () => void;
  onMonthChange?: (monthYear: string) => void;
  saving?: boolean;
  saveFeedback?: { status: 'success' | 'error'; message: string } | null;
}

const AdjustmentsBlade = ({
  open, onClose, meterId, meterName, meterNumber, monthYear,
  monthData, rateContracts = [], activeRateType = null, isApproved = false,
  energyBid, runningContractTotal, overageEmailSent = false,
  onSaveMonth, onRateSaved, onMonthChange, saving = false, saveFeedback,
}: AdjustmentsBladeProps) => {
  const monthLabel = monthYear ? dayjs(monthYear, 'YYYY-MM').format('MMMM YYYY') : '—';

  const propAdjustmentType = monthData?.shortfall != null ? 'shortfall'
    : monthData?.adHoc != null ? 'ad_hoc' : '';
  const propAmount = monthData?.shortfall != null ? String(monthData.shortfall)
    : monthData?.adHoc != null ? String(monthData.adHoc) : '';
  const propReason = monthData?.adHocReason ?? '';

  const [adjustmentType, setAdjustmentType] = useState(propAdjustmentType);
  const [amount, setAmount] = useState(propAmount);
  const [reason, setReason] = useState(propReason);
  const [reasonError, setReasonError] = useState(false);
  const [typeError, setTypeError] = useState(false);
  const [applying, setApplying] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);

  const [rateState, setRateState] = useState<{ selectedType: string | null; rows: RateRow[] }>({
    selectedType: null,
    rows: [],
  });
  const [rateCleared, setRateCleared] = useState(false);
  const [clearedRateType, setClearedRateType] = useState<string | null>(null);

  const [saveRateAdjustment] = useMutation(SAVE_RATE_ADJUSTMENT);
  const [clearRateAdjustmentMutation] = useMutation(CLEAR_RATE_ADJUSTMENT);

  // ── Overage adjustment calculation ───────────────────────────────────────
  const isCurrentMonthApproved = monthData?.approvalStatus === 'Approved';
  const thresholdLimit = (energyBid ?? 0) * 1.2;
  const isThresholdExceeded =
    runningContractTotal != null && runningContractTotal > thresholdLimit;
  const showOverageSection =
    overageEmailSent && !isCurrentMonthApproved && isThresholdExceeded;

  const contractRate =
    rateContracts.find((rc) => rc.rateType === 'Contract')?.rate ?? 0;
  const selectedOverageRateStr =
    rateState.selectedType === 'OVERAGE'
      ? (rateState.rows.find((r) => r.key === 'OVERAGE')?.rate ?? '')
      : '';
  const selectedOverageRate =
    selectedOverageRateStr !== '' ? Number(selectedOverageRateStr) : null;

  const currentMonthNetGen = monthData?.postProdStatNetGenerationKwh ?? 0;
  const prevRunningTotal = (runningContractTotal ?? 0) - currentMonthNetGen;
  const prevOverageUnits = Math.max(prevRunningTotal - thresholdLimit, 0);
  const currentOverageUnits = Math.max((runningContractTotal ?? 0) - thresholdLimit, 0);

  // Check if an Overage rate contract covered the previous month.
  // If it did not (prevOverageUnits > 0 but no rate existed), bill the full
  // cumulative overage — mirroring the backend calculateOverageAdjustment logic.
  const prevMonthYear = (() => {
    const [y, m] = (monthYear ?? '2000-01').split('-').map(Number);
    const d = new Date(y, m - 2, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  })();
  const prevMonthHadOverageRate = rateContracts.some(
    (rc) => rc.rateType === 'Overage' &&
      rc.effectiveFrom != null && rc.effectiveTo != null &&
      rc.effectiveFrom <= prevMonthYear && rc.effectiveTo >= prevMonthYear,
  );
  const overageUnits = prevOverageUnits > 0 && !prevMonthHadOverageRate
    ? currentOverageUnits                    // cumulative: prior threshold-crossing not billed
    : currentOverageUnits - prevOverageUnits; // incremental: normal case
  const calculatedOverageAdjustment =
    showOverageSection && selectedOverageRate != null
      ? (selectedOverageRate - contractRate) * overageUnits
      : null;

  const isSaving = saving || applying;
  const isDirty = !isApproved && (
    adjustmentType !== propAdjustmentType ||
    amount !== propAmount ||
    reason !== propReason ||
    rateState.selectedType !== null ||
    rateCleared
  );

  // Reset all state when blade opens or month changes
  React.useEffect(() => {
    if (open) {
      setAdjustmentType(propAdjustmentType);
      setAmount(propAmount);
      setReason(propReason);
      setReasonError(false);
      setTypeError(false);
      setInlineError(null);
      setApplying(false);
      setRateState({ selectedType: null, rows: [] });
      setRateCleared(false);
      setClearedRateType(null);
    }
  }, [open, monthYear]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-close on success; surface errors inline
  React.useEffect(() => {
    if (saveFeedback?.status === 'success') {
      const t = setTimeout(() => onClose(), 1500);
      return () => clearTimeout(t);
    }
    if (saveFeedback?.status === 'error') {
      setInlineError(saveFeedback.message);
      setApplying(false);
    }
  }, [saveFeedback, onClose]);

  const hasAdjustment = adjustmentType !== '' && amount !== '';

  const validateManualAdjustment = (): boolean => {
    let hasError = false;
    if (amount !== '' && !adjustmentType) { setTypeError(true); hasError = true; }
    if (hasAdjustment && !reason.trim()) { setReasonError(true); hasError = true; }
    return !hasError;
  };

  const validateRateAdjustment = (rateRow: RateRow | null | undefined): boolean => {
    if (!rateRow) return true;
    if (!rateRow.effectiveFrom) {
      setInlineError('Please select an Effective From date for the rate adjustment.');
      return false;
    }
    if (!rateRow.effectiveTo) {
      setInlineError('Please select an Effective To date for the rate adjustment.');
      return false;
    }
    if (rateRow.effectiveFrom.isAfter(rateRow.effectiveTo, 'month')) {
      setInlineError('Effective From must be before or equal to Effective To.');
      return false;
    }
    return true;
  };

  const buildManualAdjustmentInput = (): Record<string, any> => {
    const input: Record<string, any> = {};
    if (adjustmentType === '' && propAdjustmentType !== '') {
      if (propAdjustmentType === 'shortfall') input.shortfall = null;
      if (propAdjustmentType === 'ad_hoc') input.adHoc = null;
      input.adHocReason = null;
    } else if (adjustmentType === 'shortfall' && amount !== '' && amount !== propAmount) {
      input.shortfall = Number(amount);
      if (propAdjustmentType === 'ad_hoc') input.adHoc = null;
    } else if (adjustmentType === 'ad_hoc' && amount !== '' && amount !== propAmount) {
      input.adHoc = Number(amount);
      if (propAdjustmentType === 'shortfall') input.shortfall = null;
    }
    if (adjustmentType !== '' && reason !== propReason) input.adHocReason = reason.trim() || null;
    if (calculatedOverageAdjustment != null) {
      input.overage = Number(calculatedOverageAdjustment.toFixed(2));
    }
    return input;
  };

  const applyClearRate = async (): Promise<boolean> => {
    if (!rateCleared || !clearedRateType) return true;
    const { data, errors } = await clearRateAdjustmentMutation({
      variables: {
        meterId: Number(meterId),
        rateType: clearedRateType,
        monthYear: clearedRateType === 'RETROACTIVE' ? monthYear : undefined,
      },
    });
    if (errors?.length) { setInlineError(errors[0].message); return false; }
    if (!data?.clearRateAdjustment?.success) {
      setInlineError(data?.clearRateAdjustment?.message ?? 'Failed to clear rate adjustment.');
      return false;
    }
    onRateSaved?.();
    return true;
  };

  const applySaveRate = async (rateRow: RateRow, selectedType: string): Promise<boolean> => {
    const { data, errors } = await saveRateAdjustment({
      variables: {
        meterId: Number(meterId),
        rateType: selectedType,
        rate: Number(rateRow.rate),
        effectiveFrom: rateRow.effectiveFrom!.format('YYYY-MM'),
        effectiveTo: rateRow.effectiveTo!.format('YYYY-MM'),
        monthYear: selectedType === 'RETROACTIVE' ? monthYear : undefined,
      },
    });
    if (errors?.length) { setInlineError(errors[0].message); return false; }
    if (!data?.saveRateAdjustment?.success) {
      setInlineError(data?.saveRateAdjustment?.message ?? 'Failed to save rate adjustment.');
      return false;
    }
    onRateSaved?.();
    return true;
  };

  const handleApply = async () => {
    if (!monthYear || isApproved) return;
    setInlineError(null);

    if (!validateManualAdjustment()) return;

    const { selectedType, rows: rateRows } = rateState;
    const rateRow = selectedType ? rateRows.find((r) => r.key === selectedType) : null;
    const hasRateChange = !!(rateRow && rateRow.rate !== '');

    if (!validateRateAdjustment(hasRateChange ? rateRow : null)) return;

    const input = buildManualAdjustmentInput();
    if (Object.keys(input).length === 0 && !hasRateChange && !rateCleared) { onClose(); return; }

    setApplying(true);
    try {
      if (!await applyClearRate()) return;
      if (hasRateChange && rateRow && !await applySaveRate(rateRow, selectedType!)) return;

      if (Object.keys(input).length > 0) {
        const ok = await onSaveMonth(monthYear, input, reason.trim() || undefined);
        if (!ok) return;
      } else {
        onClose();
      }
    } catch (err: any) {
      const msg =
        err?.graphQLErrors?.[0]?.message ??
        err?.networkError?.result?.errors?.[0]?.message ??
        err?.message ??
        'An unexpected error occurred.';
      setInlineError(msg);
    } finally {
      setApplying(false);
    }
  };

  // ── Accessory: spinner + error banner rendered above the footer ───────────
  const accessoryView = (
    <>
      {isSaving && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '10px 16px', borderTop: '1px solid #f0f0f0',
          background: '#fafafa', fontSize: 13, color: '#555',
        }}>
        </div>
      )}
      {inlineError && !isSaving && (
        <div style={{
          padding: '8px 16px',
          background: '#fff2f0',
          borderTop: '1px solid #ffccc7',
          color: '#cf1322',
          fontSize: 13,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 6,
        }}>
          <span style={{ fontSize: 15, lineHeight: '20px', flexShrink: 0 }}>⚠</span>
          <span>{inlineError}</span>
        </div>
      )}
    </>
  );

  return (
    <MeterDetailsBlade
      title="Dollar Adjustments"
      applyLabel="Apply Adjustments"
      open={open}
      onClose={onClose}
      meterName={meterName}
      meterNumber={meterNumber}
      monthYear={monthYear}
      monthLabel={monthLabel}
      onMonthChange={onMonthChange}
      handleApply={handleApply}
      saving={isSaving}
      isDirty={isDirty}
      readOnly={isApproved}
      saveFeedback={saveFeedback}
      accessoryView={accessoryView}
    >
      {/* ── One-time Manual Adjustment ─────────────────────────────────── */}
      <BladeSection
        title="One-time Manual Adjustment"
        onClear={() => {
          if (isApproved) return;
          setAdjustmentType(''); setAmount(''); setReason('');
          setReasonError(false); setTypeError(false);
        }}
        showClear={(adjustmentType !== '' || amount !== '' || reason !== '') && !isApproved}
      >
        <div className={styles.inlineRow}>
          <div className={styles.inlineField} style={{ flex: '0 0 160px' }}>
            <label className={styles.inlineLabel}>
              Type{amount !== '' && <span className={styles.required}> *</span>}
            </label>
            <select
              className={[styles.select, typeError ? styles.error : ''].filter(Boolean).join(' ')}
              value={adjustmentType}
              disabled={isApproved}
              onChange={(e) => { setAdjustmentType(e.target.value); setTypeError(false); }}
            >
              <option value="" disabled>Select type</option>
              <option value="ad_hoc">Ad Hoc</option>
              <option value="shortfall">Shortfall</option>
            </select>
          </div>

          <div className={styles.inlineField} style={{ flex: 1 }}>
            <label className={styles.inlineLabel}>
              Amount{adjustmentType !== '' && <span className={styles.required}> *</span>}
            </label>
            <div className={styles.amountWrapper}>
              <span className={styles.currencyPrefix}>$</span>
              <input
                type="number"
                className={styles.amountInput}
                placeholder="enter dollar amount"
                value={amount}
                disabled={isApproved}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className={styles.reasonWrapper}>
          <div className={styles.reasonLabel}>
            Reason (Mandatory)
            {(hasAdjustment || reason !== '') && <span className={styles.required}>*</span>}
          </div>
          <input
            type="text"
            className={[styles.reasonInput, reasonError ? styles.error : ''].filter(Boolean).join(' ')}
            placeholder="Enter reason"
            maxLength={200}
            value={reason}
            disabled={isApproved}
            onChange={(e) => {
              const val = e.target.value.replace(/[^\x20-\x7E -￿\uD800-\uDFFF]/g, '');
              setReason(val);
              if (val.trim()) setReasonError(false);
            }}
          />
        </div>
      </BladeSection>

      {/* ── Rate Adjustment ────────────────────────────────────────────── */}
      <RateAdjustmentSection
        rateContracts={rateContracts}
        activeRateType={activeRateType}
        monthYear={monthYear}
        isApproved={isApproved}
        overageEmailSent={overageEmailSent}
        onChange={(selectedType, rows) => {
          setInlineError(null);
          setRateState({ selectedType, rows });
        }}
        onClear={(clearedType) => {
          setRateState({ selectedType: null, rows: [] });
          if (clearedType) {
            setRateCleared(true);
            setClearedRateType(clearedType);
          }
        }}
      />

    </MeterDetailsBlade>
  );
};

export default AdjustmentsBlade;